import { Helmet } from "react-helmet-async";
import { useEffect } from "react";

const SITE_URL = "https://govans.ie";
const SITE_NAME = "GoVans.ie";
const DEFAULT_IMAGE = "/img/van2.webp";

export default function SEO({ title, description, canonical, schema, noindex }) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Man With a Van Dublin`;
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : undefined;
  const desc = description || "GoVans.ie — Dublin's fastest man with a van. House removals, furniture delivery, IKEA collection, same-day service. From €60. Call 087 419 9541.";

  // Title is managed via document.title (NOT Helmet) to guarantee exactly ONE <title> element in the DOM.
  // Helmet's title insertion was leaving the static index.html <title> in place and prepending a second one,
  // which Screaming Frog and other JS-rendering crawlers flagged as a duplicate. Setting document.title
  // mutates the single existing <title> text node in place and reconciles cleanly on every route change.
  useEffect(() => {
    document.title = fullTitle;
  }, [fullTitle]);

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "MovingCompany",
    "@id": `${SITE_URL}/#business`,
    "name": "GoVans.ie",
    "alternateName": ["GoVans Dublin", "Go Vans"],
    "description": "Man with a van service in Dublin. House removals, furniture delivery, IKEA collection, rubbish removal, and same-day emergency service.",
    "url": SITE_URL,
    "telephone": "+353874199541",
    "email": "info@govans.ie",
    "image": `${SITE_URL}${DEFAULT_IMAGE}`,
    "logo": `${SITE_URL}/favicon-512.png`,
    "priceRange": "€€",
    "currenciesAccepted": "EUR",
    "paymentAccepted": "Cash, Bank Transfer",
    "areaServed": [
      { "@type": "City", "name": "Dublin", "containedInPlace": { "@type": "Country", "name": "Ireland" } },
      { "@type": "AdministrativeArea", "name": "County Kildare", "containedInPlace": { "@type": "Country", "name": "Ireland" } }
    ],
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Dublin",
      "addressCountry": "IE"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 53.3498,
      "longitude": -6.2603
    },
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "07:00", "closes": "21:00" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "08:00", "closes": "20:00" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": "Sunday", "opens": "09:00", "closes": "18:00" }
    ],
    "sameAs": [
      "https://www.instagram.com/govans.ie",
      "https://www.facebook.com/share/1CprtcDPvZ/",
      "https://maps.app.goo.gl/z3aZDGowkqnnBQ9S8"
    ]
  };

  return (
    <Helmet>
      <meta name="description" content={desc} />
      <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow"} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:image" content={`${SITE_URL}${DEFAULT_IMAGE}`} />
      <meta property="og:image:width" content="960" />
      <meta property="og:image:height" content="1280" />
      <meta property="og:locale" content="en_IE" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={`${SITE_URL}${DEFAULT_IMAGE}`} />

      {/* Geo */}
      <meta name="geo.region" content="IE-D" />
      <meta name="geo.placename" content="Dublin" />
      <meta name="geo.position" content="53.3498;-6.2603" />

      {/* Structured Data - Organization */}
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      {/* Page-specific schema */}
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
    </Helmet>
  );
}
